// HWM-22633: trimmed copy of hero-storefront-app's schedule/timeFormat.ts —
// just the helpers the portal slot picker needs.
//
// HWM-23070: slots now render from the response's `start_utc` instants rather
// than naive clinic wall-clock strings, so DST and cross-midnight windows are
// correct and the zone label comes from `Intl` instead of a hardcoded table.

const parseDateKey = (dateKey: string): { year: number; month: number; day: number } | null => {
    const m = dateKey.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    return { year: +m[1], month: +m[2], day: +m[3] };
};

const formatNaiveTime = (input: string): string => {
    if (!input) return '';
    const [hours, minutes] = input.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return input;
    const period = hours >= 12 ? 'PM' : 'AM';
    return `${hours % 12 || 12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// The backend may send an empty clinic timezone (`AvailabilityResult` defaults
// it to ""), and `Intl` throws on one — so callers check before doing any
// instant math and fall back to the naive clinic strings.
export const isUsableZone = (tz: string): boolean => {
    if (!tz) return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    } catch {
        return false;
    }
};

const formatInZone = (utcIso: string, tz: string, withZone: boolean): string =>
    new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour: 'numeric',
        minute: '2-digit',
        ...(withZone ? { timeZoneName: 'short' as const } : {})
    }).format(new Date(utcIso));

export const browserTimezone = (): string =>
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export const zoneLabel = (utcIso: string, tz: string): string =>
    new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(new Date(utcIso))
        .find((p) => p.type === 'timeZoneName')?.value ?? tz;

// Day keys are the clinic's business date by backend contract, so they are
// formatted from the key itself — never re-bucketed into another zone.
export const formatDayLabel = (dateKey: string): string => {
    const parts = parseDateKey(dateKey);
    if (!parts) return dateKey;
    const d = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
    const weekday = d.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short' });
    return `${weekday} ${parts.month}/${parts.day}`;
};

type SlotTime = { time: string; start_utc?: string };

const renderable = (slot: SlotTime, clinicTz: string): string | null =>
    slot.start_utc && !Number.isNaN(Date.parse(slot.start_utc)) && isUsableZone(clinicTz)
        ? slot.start_utc
        : null;

// Bare clinic-local time for the pill grid — the picker heading states the
// zone once, so repeating it on every pill is noise. Falls back to the naive
// `time` when the instant or the zone is unusable: a migration-window guard
// for an env on an older rtm-api, removed with the legacy fields.
export const formatSlotTime = (slot: SlotTime, clinicTz: string): string => {
    const iso = renderable(slot, clinicTz);
    return iso ? formatInZone(iso, clinicTz, false) : formatNaiveTime(slot.time);
};

// Same, with the zone spelled out — for the confirmation line, which is read
// away from the heading.
export const formatSlotWithZone = (slot: SlotTime, clinicTz: string): string => {
    const iso = renderable(slot, clinicTz);
    return iso ? formatInZone(iso, clinicTz, true) : formatNaiveTime(slot.time);
};

// The care manager's own clock for the same instant — returned only when it
// reads differently from the clinic rendering, so a CM already in the clinic's
// zone (or an equivalent one, e.g. America/Detroit) sees no redundant line.
// Time-of-day only: the clinic's booking window never crosses midnight for a
// US-based care manager, which is the only staffing this serves today.
export const formatViewerEquivalent = (utcIso: string, clinicTz: string): string | null => {
    if (Number.isNaN(Date.parse(utcIso)) || !isUsableZone(clinicTz)) return null;
    const viewer = formatInZone(utcIso, browserTimezone(), true);
    return viewer === formatInZone(utcIso, clinicTz, true) ? null : viewer;
};
